/**
 * Liste déroulante à sélection multiple (Studios, Coachs : SPEC PL10, P7), sur chakra-react-select comme la v1
 * (Filters/Studios.tsx, Filters/Coaches.tsx) : recherche au clavier, options groupées par marque en vue
 * multi-marques. Le contrôle garde la taille d'une pilule de la maquette (`.select`) : il affiche un résumé
 * (« Tous les studios », « Louvre », « 3 studios ») au lieu d'une étiquette par valeur choisie.
 *
 * Retours du 12/10 (Daphné) : option « Tous les studios » / « Tous les coachs » en tête du menu, en vue simple comme en
 * vue multi-marques groupée (avant les groupes) : un clic (ou Entrée) vide la sélection et ferme le menu ; cochée quand
 * rien n'est choisi (lib/planning/filterSelect.ts). Messages du lecteur d'écran en français.
 */
import { Select, type ChakraStylesConfig } from 'chakra-react-select';
import {
  useMemo,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import type { FilterOption, FilterOptionGroup } from '../../lib/planning/filters';
import {
  frenchAriaLiveMessages,
  isAllOption,
  isOptionChecked,
  menuOptions,
  resultsStatus,
  valueAfterChange,
} from '../../lib/planning/filterSelect';

interface FilterSelectProps {
  id: string;
  /** Libellé accessible (« Studios »). */
  label: string;
  /** Résumé sans sélection (« Tous les studios »). */
  allLabel: string;
  /** Résumé de plusieurs valeurs (n ≥ 2 → « 3 studios »). */
  manyLabel: (count: number) => string;
  /** Nom d'une valeur absente des options (liste pas encore chargée), connu ailleurs (cours déjà chargés). */
  labelFor?: (value: string) => string | undefined;
  groups: FilterOptionGroup[];
  /** Vue multi-marques : en-têtes de groupe avec la pastille de la marque. */
  grouped: boolean;
  colorOf: (siteId: string) => string;
  value: string[];
  onChange: (value: string[]) => void;
  isDisabled?: boolean;
  /**
   * Bord du contrôle sur lequel le menu s'aligne. « end » pour la liste de droite (Coachs) : son menu, plus large
   * que le contrôle (240 px), s'ouvrait vers la droite et sortait de la barre de filtres, et de l'écran à 390 px
   * (page qui défile en largeur, revue du 04/10).
   */
  menuAlign?: 'start' | 'end';
  /** Menu ouvert au premier rendu (rendu de contrôle des tests : FilterSelect.test.tsx). */
  defaultMenuOpen?: boolean;
}

export function FilterSelect({
  id,
  label,
  allLabel,
  manyLabel,
  labelFor,
  groups,
  grouped,
  colorOf,
  value,
  onChange,
  isDisabled,
  menuAlign = 'start',
  defaultMenuOpen = false,
}: FilterSelectProps) {
  const flat = useMemo(() => groups.flatMap((group) => group.options), [groups]);
  const selected = useMemo(() => flat.filter((option) => value.includes(option.value)), [flat, value]);
  // Saisie de la recherche au clavier (react-select la garde ; recopiée par onInputChange, plus bas).
  const [input, setInput] = useState('');
  // « Tous les studios » / « Tous les coachs » en tête du menu (retours du 12/10), avant les groupes en vue groupée ;
  // pas pendant une recherche (relecture du 12/10 : Entrée après « s » vidait la sélection, lib/planning/filterSelect.ts).
  const searching = input.trim() !== '';
  const options = useMemo(() => menuOptions(groups, grouped, allLabel, searching), [groups, grouped, allLabel, searching]);
  const ariaLiveMessages = useMemo(() => frenchAriaLiveMessages(label), [label]);
  // Menu piloté : « Tous » le ferme ; les autres choix le laissent ouvert (closeMenuOnSelect à false, plus bas).
  const [menuOpen, setMenuOpen] = useState(defaultMenuOpen);
  // Une valeur choisie peut manquer des options (liste pas encore chargée) : son nom vient des cours déjà chargés,
  // sinon une place réservée, sans texte (revue du 05/10 : « 1 studios » puis « Oberkampf » 1,7 s plus tard).
  const count = value.length;
  const single = count === 1 ? (selected[0]?.label ?? labelFor?.(value[0])) : undefined;
  const summary: ReactNode =
    count === 0 ? (
      allLabel
    ) : count > 1 ? (
      manyLabel(count)
    ) : single ? (
      single
    ) : (
      <>
        <span className="mmv2-filter-select__pending" aria-hidden="true" />
        <span className="mmv2-sr-only">Chargement…</span>
      </>
    );

  const chakraStyles = useMemo<ChakraStylesConfig<FilterOption, true, FilterOptionGroup>>(
    () => ({
      container: (provided) => ({ ...provided, minW: { base: '0', md: '196px' }, w: { base: 'full', md: 'auto' } }),
      control: (provided, state) => ({
        ...provided,
        // 34 px : la hauteur des étiquettes de la même rangée (maquette : .chip et .select à 34 px).
        minH: '34px',
        h: '34px',
        bg: 'mmv2.paper',
        borderWidth: '1.5px',
        borderColor: state.isFocused || count > 0 ? 'mmv2.ink' : 'mmv2.line',
        borderRadius: 'mmv2Pill',
        boxShadow: 'none',
        fontFamily: 'mmv2',
        fontSize: '12.5px',
        // Boîte de ligne = zone de contenu de la police : texte centré au pixel (styles/ux-v2.css, contrôles).
        lineHeight: 'normal',
        cursor: 'pointer',
        _hover: { borderColor: state.isFocused || count > 0 ? 'mmv2.ink' : 'mmv2.ink4' },
        // Focus : la bordure reste à l'encre ; le focus clavier prend le contour des autres contrôles (2,5 px décalé de
        // 2 px, planning.css), au lieu de la bordure de focus + ombre de 1 px du champ Chakra (anneau collé au bord).
        _focusVisible: { borderColor: 'mmv2.ink', boxShadow: 'none' },
        // Filtre figé (mode coach) : une information, pas un champ grisé. L'opacité de 40 % du champ Chakra
        // désactivé rendait le nom du coach illisible (2,65:1, axe).
        _disabled: { opacity: 1, cursor: 'default', bg: 'mmv2.sunken', borderColor: 'mmv2.line' },
      }),
      valueContainer: (provided) => ({ ...provided, pl: '14px', pr: '4px' }),
      placeholder: (provided) => ({
        ...provided,
        color: count > 0 ? 'mmv2.ink' : 'mmv2.ink2',
        fontWeight: count > 0 ? 600 : 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
      input: (provided) => ({ ...provided, color: 'mmv2.ink' }),
      // Largeur libre : la largeur fixe de l'icône du thème Select (24 px) moins les marges intérieures ne laissait
      // aucune place au chevron (invisible).
      dropdownIndicator: (provided, state) => ({
        ...provided,
        display: state.selectProps.isDisabled ? 'none' : provided.display,
        w: 'auto',
        mx: 0,
        bg: 'transparent',
        px: '12px',
        color: 'mmv2.ink3',
      }),
      indicatorSeparator: () => ({ display: 'none' }),
      // Jamais plus large que l'écran moins ses marges (16 px de chaque côté), calé sur le bord choisi du contrôle.
      menu: (provided) => ({
        ...provided,
        zIndex: 30,
        my: '6px',
        minW: 'min(240px, calc(100vw - 32px))',
        maxW: 'calc(100vw - 32px)',
        ...(menuAlign === 'end' ? { left: 'auto', right: 0 } : {}),
      }),
      menuList: (provided) => ({
        ...provided,
        py: '6px',
        maxH: '340px',
        bg: 'mmv2.paper',
        borderWidth: '1px',
        borderColor: 'mmv2.line2',
        borderRadius: '14px',
        boxShadow: 'mmv2Lg',
      }),
      group: (provided) => ({ ...provided, py: '4px' }),
      groupHeading: (provided) => ({
        ...provided,
        px: '14px',
        py: '6px',
        fontFamily: 'mmv2',
        lineHeight: 'normal',
        fontSize: '10.5px',
        fontWeight: 600,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        color: 'mmv2.ink3',
        bg: 'mmv2.paper',
      }),
      // paddingX / paddingY : les clés que pose chakra-react-select pour la taille sm (0,6 / 0,3 rem). Des clés px / py
      // déjà présentes plus haut dans l'objet (styles du thème Menu) gardaient leur place et perdaient contre elles :
      // 9,6 / 4,8 px calculés au lieu de 14 / 8 (revue du 04/10). Interligne « normal » comme les autres contrôles :
      // l'interligne 1,6 hérité du thème mettait le texte 1,2 à 1,7 px au-dessus du centre de l'option (Chromium).
      option: (provided, state) => ({
        ...provided,
        paddingX: '14px',
        paddingY: '8px',
        fontFamily: 'mmv2',
        fontSize: '13px',
        lineHeight: 'normal',
        color: 'mmv2.ink',
        fontWeight: state.isSelected ? 600 : 400,
        bg: state.isFocused ? 'mmv2.sunken' : 'transparent',
        _active: { bg: 'mmv2.sunken' },
        // « Tous » : séparé des studios / coachs (ou des groupes de marques) par un filet, comme un en-tête de liste.
        ...(isAllOption(state.data)
          ? { borderBottomWidth: '1px', borderBottomColor: 'mmv2.line', mb: '4px', paddingY: '9px' }
          : {}),
      }),
      noOptionsMessage: (provided) => ({ ...provided, fontSize: '13px', color: 'mmv2.ink3' }),
    }),
    [count, menuAlign],
  );

  /*
   * Au doigt (pointeur grossier) : la liste reste ouverte après un choix, pour en cocher plusieurs (react-select sortait
   * du champ et fermait la liste à chaque choix au toucher ; revue du 05/10), et sans recherche (le clavier de l'écran
   * ne s'ouvre pas sur la liste). Un tap hors de la liste la ferme.
   */
  const coarse =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;

  /*
   * Modalité de saisie (planning.css : contour de focus au clavier seulement). Attribut posé directement sur le
   * conteneur (pas d'état React : aucun rendu de plus) au clic / toucher, retiré à la première touche (Échap
   * exceptée : fermer la liste ouverte à la souris ne fait pas apparaître l'anneau) ou quand le focus quitte la liste.
   */
  const markPointer = (event: SyntheticEvent<HTMLDivElement>) => {
    event.currentTarget.dataset.pointer = '';
  };
  const markKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') delete event.currentTarget.dataset.pointer;
  };
  const leave = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) delete event.currentTarget.dataset.pointer;
  };

  return (
    <div
      className="mmv2-filter-select"
      data-filter={id}
      onMouseDownCapture={markPointer}
      onTouchStartCapture={markPointer}
      onKeyDownCapture={markKeyboard}
      onBlur={leave}
    >
      <label className="mmv2-sr-only" htmlFor={`mmv2-${id}`}>
        {label}
      </label>
      <Select<FilterOption, true, FilterOptionGroup>
        inputId={`mmv2-${id}`}
        instanceId={`mmv2-${id}`}
        className={`mmv2-select mmv2-select--${id}`}
        classNamePrefix="mmv2-select"
        isMulti
        size="sm"
        options={options}
        value={selected}
        isOptionSelected={isOptionChecked}
        onInputChange={(next) => setInput(next)}
        onChange={(next, meta) => {
          const result = valueAfterChange(next ?? [], meta.option, value);
          if (result.changed) onChange(result.value);
          if (result.close) setMenuOpen(false);
        }}
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        ariaLiveMessages={ariaLiveMessages}
        screenReaderStatus={resultsStatus}
        placeholder={summary}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        isSearchable={!coarse}
        isClearable={false}
        backspaceRemovesValue={false}
        selectedOptionStyle="check"
        focusBorderColor="mmv2.focus"
        menuPlacement="auto"
        isDisabled={isDisabled}
        noOptionsMessage={() => 'Aucun résultat'}
        // Classe hors de l'espace de noms de react-select (classNamePrefix « mmv2-select ») : « mmv2-select__group »
        // est aussi la classe du conteneur de chaque groupe, qui prenait la règle du libellé (inline-flex) et posait
        // l'en-tête à gauche des options (revue du 04/10, défaut présent depuis dc4c1cd).
        formatGroupLabel={(group) => (
          <span className="mmv2-filter-group-label">
            <span className="mmv2-chip__dot" style={{ '--mmv2-dot': colorOf(group.siteId) } as CSSProperties} />
            {group.label}
          </span>
        )}
        chakraStyles={chakraStyles}
      />
    </div>
  );
}
